import { useCallback, useRef, useState } from 'react';
import { TripWithTenant } from '../../../../types';

export interface ScheduleSelectionResult {
  selectedIds: Set<string>;
  selectedTrips: TripWithTenant[];
  isAllSelected: boolean;
  toggleSelection: (tripId: string, shiftKey?: boolean) => void;
  selectAll: () => void;
  clearSelection: () => void;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

/**
 * Hook for managing trip selection in the Schedule table.
 * Supports single selection, range selection (Shift+click), and select all.
 *
 * @param trips - Array of trips to select from (determines order for range selection)
 * @returns Selection state and handlers
 */
export function useScheduleSelection(
  trips: TripWithTenant[]
): ScheduleSelectionResult {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const lastSelectedIdRef = useRef<string | null>(null);

  // Get ordered trip IDs for range selection
  const orderedTripIds = trips.map((t) => t.trip_id);

  // Compute selected trips from IDs
  const selectedTrips = trips.filter((t) => selectedIds.has(t.trip_id));

  const handleRangeSelect = useCallback(
    (tripId: string) => {
      if (!lastSelectedIdRef.current) {
        // No previous selection, just select this one
        setSelectedIds(new Set([tripId]));
        lastSelectedIdRef.current = tripId;
        return;
      }

      const startIdx = orderedTripIds.indexOf(lastSelectedIdRef.current);
      const endIdx = orderedTripIds.indexOf(tripId);

      if (startIdx === -1 || endIdx === -1) {
        // Fallback to single selection if IDs not found
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          newSet.add(tripId);
          return newSet;
        });
        return;
      }

      // Get all IDs in the range (inclusive)
      const [from, to] = startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
      const rangeIds = orderedTripIds.slice(from, to + 1);

      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        rangeIds.forEach((id) => newSet.add(id));
        return newSet;
      });
    },
    [orderedTripIds]
  );

  const toggleSelection = useCallback(
    (tripId: string, shiftKey = false) => {
      if (shiftKey && lastSelectedIdRef.current) {
        // Range selection with Shift+click
        handleRangeSelect(tripId);
      } else {
        // Single toggle
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(tripId)) {
            newSet.delete(tripId);
          } else {
            newSet.add(tripId);
          }
          return newSet;
        });
        lastSelectedIdRef.current = tripId;
      }
    },
    [handleRangeSelect]
  );

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(orderedTripIds));
    lastSelectedIdRef.current = null;
  }, [orderedTripIds]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    lastSelectedIdRef.current = null;
  }, []);

  const isAllSelected = trips.length > 0 && selectedIds.size === trips.length;

  return {
    selectedIds,
    selectedTrips,
    isAllSelected,
    toggleSelection,
    selectAll,
    clearSelection,
    setSelectedIds,
  };
}
